import type { ReactNode } from "react";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { BreadcrumbJsonLd } from "@/components/shared/json-ld";
import { SlimFooter, SubHero, SubMasthead } from "@/components/bluebird/Chrome";
import { Button, H2 } from "@/components/bluebird/primitives";
import { color, maxWidth, radius, space } from "@/lib/design-tokens";
import { shop } from "@/lib/copy/shop";
import { service } from "@/lib/copy/service";

/**
 * `/services` is the canonical URL; `/service` 301s to it (config/routes.rb,
 * covered by test/integration/redirects_test.rb). The nav item reads "Service"
 * but points here.
 */
function Services() {
  return (
    <>
      {/* Structured data only — renders no visible markup, so it cannot move a
          pixel in script/design_diff.mjs. Kept from the previous design because
          breadcrumbs are what let a search result show the path to the page. */}
      <BreadcrumbJsonLd items={[{ name: "Service", href: "/services" }]} />

      <SubMasthead
        current="/services"
        photo="service-bench"
        photoAlt="The service bench at the shop, tools on the wall and a cargo bike on the floor"
        focalPosition="center 48%"
      >
        <SubHero
          line1={service.hero.line1}
          line2={service.hero.line2}
          intro={service.hero.intro}
          action={
            <Button
              href="#service-contact"
              variant="goldSoft"
              style={{ fontSize: "16px", padding: "15px 26px" }}
            >
              {service.hero.cta}
            </Button>
          }
        />
      </SubMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>

      <section
        data-section="costs"
        style={{ maxWidth, margin: "0 auto", padding: `${space.sectionY} ${space.gutter}` }}
      >
        <H2 data-reveal style={{ margin: "0 0 32px" }}>
          {service.costs.heading}
        </H2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(260px,100%),1fr))",
            gap: "clamp(18px,2.5vw,28px)",
          }}
        >
          {service.costs.cards.map((card) => (
            <div
              key={card.figure}
              data-reveal
              data-reveal-stagger=""
              style={{
                border: `1px solid ${color.border}`,
                borderRadius: radius.panel,
                padding: "28px",
              }}
            >
              <div style={{ fontWeight: 800, fontSize: "clamp(30px,4vw,42px)" }}>{card.figure}</div>
              {card.title && (
                <div style={{ fontWeight: 700, fontSize: "18px", marginTop: "6px" }}>
                  {card.title}
                </div>
              )}
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: color.slate,
                }}
              >
                {card.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section data-section="how" style={{ background: color.blue, color: color.white }}>
        <div
          style={{
            maxWidth,
            margin: "0 auto",
            padding: `${space.sectionY} ${space.gutter}`,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))",
            gap: "clamp(32px,5vw,72px)",
            alignItems: "center",
          }}
        >
          <div>
            <H2 data-reveal>{service.how.heading}</H2>
            <div
              data-reveal
              data-reveal-delay="120"
              style={{
                marginTop: "24px",
                fontSize: "16.5px",
                lineHeight: 1.6,
                color: color.onBlue,
                display: "grid",
                gap: "18px",
              }}
            >
              {service.how.steps.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "16px" }}>
                  <span style={{ color: color.gold, fontWeight: 800, flex: "none" }}>{i + 1}</span>
                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    {step.before}
                    {step.after}
                  </span>
                </div>
              ))}
            </div>
            <Button
              href={shop.phoneHref}
              variant="goldSoft"
              style={{ marginTop: "30px", fontSize: "16px", padding: "15px 26px" }}
            >
              {service.how.cta}
            </Button>
          </div>
          <div
            id="service-contact"
            data-service-schedule=""
            style={{ background: color.white, color: color.ink, borderRadius: radius.panel, overflow: "hidden" }}
          >
            <img
              src="/images/place/service-bike-on-lift-1400.jpg"
              srcSet={
                "/images/place/service-bike-on-lift-900.jpg 900w, " +
                "/images/place/service-bike-on-lift-1400.jpg 1400w, " +
                "/images/place/service-bike-on-lift-2000.jpg 2000w"
              }
              sizes="(max-width: 700px) 92vw, 560px"
              width="2000"
              height="2666"
              loading="lazy"
              decoding="async"
              alt="A turquoise cargo bike raised on the service lift inside Front Range Cargo Bikes"
              style={{
                width: "100%",
                aspectRatio: "4/3",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
            />
            <div style={{ padding: "clamp(20px,3vw,32px)" }}>
              <H2 style={{ fontSize: "clamp(24px,3vw,32px)" }}>{service.how.scheduleHeading}</H2>
              <p style={{ margin: "10px 0 0", fontSize: "16px", lineHeight: 1.55, color: color.inkMuted }}>
                {service.how.scheduleBody}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "22px" }}>
                <Button href={shop.phoneHref} variant="blue" style={{ fontSize: "15px", padding: "13px 20px" }}>
                  {service.how.callCta}
                </Button>
                <Button href={shop.smsHref} variant="outlineBlue" style={{ fontSize: "15px", padding: "12px 20px" }}>
                  {service.how.textCta}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        data-section="elsewhere"
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `${space.sectionY} ${space.gutter}`,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(280px,100%),1fr))",
          gap: "clamp(28px,4vw,56px)",
          alignItems: "center",
        }}
      >
        <div>
          <H2 data-reveal>{service.elsewhere.heading}</H2>
          <p style={{ margin: "16px 0 0", fontSize: "16.5px", lineHeight: 1.6, color: color.slate }}>
            {service.elsewhere.body}
          </p>
        </div>
        <div style={{ justifySelf: "start" }}>
          <Button
            href="/contact?about=service"
            variant="outlineBlue"
            style={{
              border: `2px solid ${color.blue}`,
              fontSize: "16px",
              padding: "13px 26px",
            }}
          >
            {service.elsewhere.cta}
          </Button>
        </div>
      </section>

      </main>

      <SlimFooter />
    </>
  );
}

Services.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default Services;
